// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import HubIcon from '@mui/icons-material/Hub'
import StorageIcon from '@mui/icons-material/Storage'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import BulletGrid from '@/components/sections/BulletGrid'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

type Block = {
  icon: ReactElement
  title: string
  /** Body copy, rendered in order. */
  paragraphs: string[]
  /** Only some blocks still carry a list; it is rendered after the copy. */
  items?: string[]
  /** Closing remark set apart from the copy above it. */
  note?: string
}

export default function ConnectivitySection() {
  const { t } = useTranslation('servicesTestEquipmentModernization')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const iconSx = { fontSize: '1.9rem', color: themeColors.bg.start }

  const blocks: Block[] = [
    {
      icon: <HubIcon sx={iconSx} />,
      title: t('connectivity.blocks.01.title'),
      paragraphs: [
        t('connectivity.blocks.01.p1'),
        t('connectivity.blocks.01.p2'),
        t('connectivity.blocks.01.p3')
      ]
    },
    {
      icon: <StorageIcon sx={iconSx} />,
      title: t('connectivity.blocks.02.title'),
      paragraphs: [
        t('connectivity.blocks.02.p1'),
        t('connectivity.blocks.02.p2'),
        t('connectivity.blocks.02.p3')
      ]
    },
    {
      icon: <QrCode2Icon sx={iconSx} />,
      title: t('connectivity.blocks.03.title'),
      paragraphs: [
        t('connectivity.blocks.03.p1'),
        t('connectivity.blocks.03.p2')
      ],
      items: [
        t('connectivity.blocks.03.items.01'),
        t('connectivity.blocks.03.items.02'),
        t('connectivity.blocks.03.items.03'),
        t('connectivity.blocks.03.items.04')
      ],
      note: t('connectivity.blocks.03.note')
    },
    {
      icon: <DisplaySettingsIcon sx={iconSx} />,
      title: t('connectivity.blocks.04.title'),
      paragraphs: [
        t('connectivity.blocks.04.p1'),
        t('connectivity.blocks.04.p2'),
        t('connectivity.blocks.04.p3')
      ]
    }
  ]

  const getGrid = () => {
    if (w > breakpoints.desktopToMobile) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getBlock = (block: Block) => (
    <Box
      key={block.title}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.75rem',
        boxSizing: 'border-box'
      }}
    >
      <Box sx={{ lineHeight: 0, marginBottom: '1.1rem' }}>{block.icon}</Box>

      <Typography
        variant="h3"
        component="h3"
        sx={{
          fontSize: '1.05rem',
          fontWeight: 700,
          lineHeight: 1.3,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '0.9rem'
        }}
      >
        {block.title}
      </Typography>

      {block.paragraphs.map(paragraph => (
        <Typography
          key={paragraph}
          component="p"
          sx={{
            fontSize: getFontBodySmall(),
            lineHeight: 1.7,
            color: themeColors.gray.defaultText,
            marginBottom: '0.9rem',
            '&:last-of-type': { marginBottom: 0 }
          }}
        >
          {paragraph}
        </Typography>
      ))}

      {block.items && (
        <Box sx={{ marginTop: '1.1rem' }}>
          <BulletGrid items={block.items} columns={2} />
        </Box>
      )}

      {block.note && (
        <Typography
          component="p"
          sx={{
            marginTop: '1.25rem',
            paddingLeft: '0.9rem',
            borderLeft: `3px solid ${themeColors.success.main}`,
            fontSize: getFontBodySmall(),
            lineHeight: 1.7,
            color: themeColors.gray.defaultText
          }}
        >
          {block.note}
        </Typography>
      )}
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('connectivity.eyebrow')}
        title={t('connectivity.title')}
        intro={[
          t('connectivity.intro1'),
          t('connectivity.intro2'),
          t('connectivity.intro3')
        ]}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {blocks.map(block => getBlock(block))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
